import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { peekQueryClient } from "@/queries/client";
import {
  invalidateCachedRequestWorkflow,
  invalidateCachedRequestWorkflowActivity,
} from "@/queries/request-workflows";
import { patchCachedRequest } from "@/queries/requests";
import type { RootStore } from "./root.store";
import type { TRequestWorkflow, TRequestWorkflowPendingTransition } from "@/types/request-workflow";
import type {
  TRequestUpdateWorkflowStateData,
  TRequestCreateWorkflowTransitionData,
  TRequestUpdateWorkflowTransitionData,
  TRequestSetWorkflowMoversData,
  TRequestSetWorkflowApproversData,
  TRequestWorkflowApprovalData,
} from "@/services/request-workflow.service";

/**
 * The request workflow rule and approval writers (NG-2809). It holds no server
 * data: the rules, their activity log and each request's pending approvals are
 * read through Query (`queries/request-workflows.ts`, `pending_transitions` on
 * the request row), and a write here re-reads or patches those entries.
 */
export class RequestWorkflowStore {
  isMutating = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** After a rule write: re-read the form's rules and its activity. */
  private async reloadRules(slug: string, formId: string): Promise<void> {
    const client = peekQueryClient();
    if (client) await invalidateCachedRequestWorkflow(client, slug, formId);
  }

  /**
   * Re-read one request's pending approvals after a vote, a request or a
   * cancel. They ride on the request row, so the answer is merged into every
   * cached copy of it, and the form's activity log is re-read.
   */
  async refreshPendingForSubmission(
    slug: string,
    formId: string,
    submissionId: string,
  ): Promise<TRequestWorkflowPendingTransition[]> {
    const pending = await this.rootStore.requestWorkflowService.listPendingTransitions(
      slug,
      formId,
      submissionId,
    );
    const client = peekQueryClient();
    if (client) {
      patchCachedRequest(client, slug, submissionId, { pending_transitions: pending });
      void invalidateCachedRequestWorkflowActivity(client, slug, formId);
    }
    return pending;
  }

  async updateWorkflowState(
    slug: string,
    formId: string,
    stateId: string,
    data: TRequestUpdateWorkflowStateData,
    source: AnalyticsSource = "page",
  ): Promise<TRequestWorkflow> {
    this.isMutating = true;
    try {
      const updated = await this.rootStore.requestWorkflowService.updateWorkflowState(
        slug,
        formId,
        stateId,
        data,
      );
      const client = peekQueryClient();
      if (client) void invalidateCachedRequestWorkflow(client, slug, formId);
      track("request_workflow_state_updated", {
        form_id: formId,
        state_id: stateId,
        fields: Object.keys(data),
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async resetWorkflow(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.resetWorkflow(slug, formId);
      // Each configuration write reports before its refetch, like the approval
      // events below: the write landed even if reloading the workflow fails.
      track("request_workflow_reset", { form_id: formId, source });
      await this.reloadRules(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async createTransition(
    slug: string,
    formId: string,
    data: TRequestCreateWorkflowTransitionData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.createTransition(slug, formId, data);
      track("request_workflow_transition_created", {
        form_id: formId,
        workflow_id: data.workflow_id,
        to_status_id: data.transition_status_id,
        source,
      });
      await this.reloadRules(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateTransition(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestUpdateWorkflowTransitionData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.updateTransition(
        slug,
        formId,
        transitionId,
        data,
      );
      // Each control sends only its own setting, so the body's keys are the change.
      track("request_workflow_transition_updated", {
        form_id: formId,
        transition_id: transitionId,
        fields: Object.keys(data),
        source,
      });
      await this.reloadRules(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async deleteTransition(
    slug: string,
    formId: string,
    transitionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.deleteTransition(slug, formId, transitionId);
      track("request_workflow_transition_deleted", {
        form_id: formId,
        transition_id: transitionId,
        source,
      });
      await this.reloadRules(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setMovers(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestSetWorkflowMoversData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.setMovers(slug, formId, transitionId, data);
      track("request_workflow_transition_updated", {
        form_id: formId,
        transition_id: transitionId,
        fields: ["movers"],
        source,
      });
      await this.reloadRules(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setApprovers(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestSetWorkflowApproversData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.setApprovers(slug, formId, transitionId, data);
      track("request_workflow_transition_updated", {
        form_id: formId,
        transition_id: transitionId,
        fields: ["approvers"],
        source,
      });
      await this.reloadRules(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async submitApproval(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestWorkflowApprovalData,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      const result = await this.rootStore.requestWorkflowService.submitApproval(
        slug,
        formId,
        transitionId,
        data,
      );
      this.trackApprovalEvent("request_approval_submitted", transitionId, data, source);
      if (data.submission_id) {
        await this.refreshPendingForSubmission(slug, formId, data.submission_id);
        // When this vote tipped the transition over its approval threshold, the
        // backend executed the state move in the same request. Reflect it on the
        // board now so the card lands in its destination column without a refresh.
        if (result.transition_executed && result.new_status_id) {
          await this.rootStore.intakeStore.relocateSubmissionToState(
            slug,
            data.submission_id,
            result.new_status_id,
          );
        }
      }
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async submitRejection(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestWorkflowApprovalData,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      const result = await this.rootStore.requestWorkflowService.submitRejection(
        slug,
        formId,
        transitionId,
        data,
      );
      this.trackApprovalEvent("request_rejection_submitted", transitionId, data, source);
      if (data.submission_id) {
        await this.refreshPendingForSubmission(slug, formId, data.submission_id);
        // A rejection moves the submission to the transition's configured
        // rejection_state when one is set. Relocate the card the same way an
        // approval does so the board reflects it without a refresh.
        if (result.rejection_status_id) {
          await this.rootStore.intakeStore.relocateSubmissionToState(
            slug,
            data.submission_id,
            result.rejection_status_id,
          );
        }
      }
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async requestApproval(
    slug: string,
    formId: string,
    transitionId: string,
    submissionId: string,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.requestApproval(
        slug,
        formId,
        transitionId,
        submissionId,
      );
      track("request_approval_requested", {
        request_id: submissionId,
        transition_id: transitionId,
        source,
      });
      await this.refreshPendingForSubmission(slug, formId, submissionId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async cancelApprovalRequest(
    slug: string,
    formId: string,
    transitionId: string,
    submissionId: string,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.cancelApprovalRequest(
        slug,
        formId,
        transitionId,
        submissionId,
      );
      track("request_approval_cancelled", {
        request_id: submissionId,
        transition_id: transitionId,
        source,
      });
      await this.refreshPendingForSubmission(slug, formId, submissionId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /** An approver's vote, emitted as soon as the write lands: the pending and
   *  board refreshes after it are reads, and their failing must not lose it. */
  private trackApprovalEvent(
    event: "request_approval_submitted" | "request_rejection_submitted",
    transitionId: string,
    data: TRequestWorkflowApprovalData,
    source: string,
  ): void {
    track(event, {
      request_id: data.submission_id ?? null,
      transition_id: transitionId,
      source,
    });
  }
}
